// ─── CONVERSATION LINK PANEL ────────────────────────────────────────────────
// Reusable modal for linking/unlinking the current DM or RP story to other
// DMs/RPs. Mounted from both DMView and the RP shell. When two conversations
// are linked, the prompt builder pulls compressed, relevance-gated memory
// from the linked one (see conversationLinks.js).

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getLinkedConversationRefs,
  listAllLinkableConversations,
  linkConversation,
  unlinkConversation,
} from './conversationLinks.js';

export default function ConversationLinkPanel({
  currentType,      // 'dm' | 'story'
  currentId,
  currentTitle,     // for the header
  userId,
  onClose,
  theme = {},
  isMobile = false,
}) {
  const t = theme;
  const [loading, setLoading] = useState(true);
  const [all, setAll] = useState([]);                 // every linkable convo
  const [linkedKeys, setLinkedKeys] = useState(new Set()); // Set<"type:id">
  const [busyKey, setBusyKey] = useState(null);       // row mid-toggle
  const [filter, setFilter] = useState('');

  const keyOf = (type, id) => `${type}:${id}`;

  const load = useCallback(async () => {
    if (!userId || !currentId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [refs, items] = await Promise.all([
        getLinkedConversationRefs(currentType, currentId, userId),
        listAllLinkableConversations(userId, currentType, currentId),
      ]);
      setLinkedKeys(new Set(refs.map(r => keyOf(r.type, r.id))));
      setAll(items);
    } catch (err) {
      console.warn('[ConversationLinkPanel] load failed:', err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [userId, currentId, currentType]);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (item) => {
    const k = keyOf(item.type, item.id);
    if (busyKey) return;
    setBusyKey(k);
    const isLinked = linkedKeys.has(k);
    // Optimistic flip
    setLinkedKeys(prev => {
      const next = new Set(prev);
      if (isLinked) next.delete(k); else next.add(k);
      return next;
    });
    const fn = isLinked ? unlinkConversation : linkConversation;
    const res = await fn(currentType, currentId, item.type, item.id, userId);
    if (!res.ok) {
      // Roll back on failure
      setLinkedKeys(prev => {
        const next = new Set(prev);
        if (isLinked) next.add(k); else next.delete(k);
        return next;
      });
      alert(`⚠️ Couldn't ${isLinked ? 'unlink' : 'link'} "${item.title}": ${res.error || 'unknown error'}`);
    }
    setBusyKey(null);
  }, [busyKey, linkedKeys, currentType, currentId, userId]);

  const { linked, unlinked } = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const match = (it) => !f || it.title.toLowerCase().includes(f);
    const linkedList = [];
    const unlinkedList = [];
    for (const it of all) {
      if (!match(it)) continue;
      if (linkedKeys.has(keyOf(it.type, it.id))) linkedList.push(it);
      else unlinkedList.push(it);
    }
    return { linked: linkedList, unlinked: unlinkedList };
  }, [all, linkedKeys, filter]);

  const accentGold = t.accentGold || '#c4a882';
  const textMuted = t.textMuted || '#9a8a8a';

  const row = (it) => {
    const k = keyOf(it.type, it.id);
    const isLinked = linkedKeys.has(k);
    const isBusy = busyKey === k;
    return (
      <div key={k} style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px 12px', borderRadius: 'var(--ds-r-md, 14px)',
        background: isLinked ? 'rgba(122,162,247,0.08)' : 'var(--ds-surface-2, #1d1013)',
        border: `1px solid ${isLinked ? 'rgba(122,162,247,0.3)' : 'var(--ds-line-soft, #2a161b)'}`,
        marginBottom: '6px',
      }}>
        <span style={{ fontSize: '15px', flexShrink: 0 }}>{it.type === 'dm' ? '💬' : '📖'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ds-ink-hi, #f1e7e3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {it.title}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--ds-ink-low, #9c8983)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {it.type === 'dm' ? 'Direct Message' : 'RP Story'}
          </div>
        </div>
        <button
          onClick={() => toggle(it)}
          disabled={isBusy}
          style={{
            flexShrink: 0, minHeight: '44px', minWidth: '76px',
            padding: '7px 12px', borderRadius: 'var(--ds-r-sm, 10px)', cursor: isBusy ? 'wait' : 'pointer',
            fontSize: '11px', fontWeight: 700, fontFamily: "'Cinzel', serif", letterSpacing: '0.5px',
            background: isLinked ? 'var(--ds-accent-a1f, rgba(198,40,40,0.12))' : 'rgba(122,162,247,0.15)',
            color: isLinked ? 'var(--ds-danger, #d6485e)' : '#7aa2f7',
            border: `1px solid ${isLinked ? 'var(--ds-accent-a59, rgba(198,40,40,0.35))' : 'rgba(122,162,247,0.4)'}`,
            opacity: isBusy ? 0.6 : 1,
            touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none',
          }}
        >
          {isBusy ? '…' : isLinked ? '✕ Unlink' : '🔗 Link'}
        </button>
      </div>
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? '0' : '24px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? '100%' : '460px', maxWidth: '100%',
          maxHeight: isMobile ? '85vh' : '78vh',
          background: 'var(--ds-glass, rgba(29, 16, 19, 0.77))',
          border: '1px solid var(--ds-glass-edge, #c4a8823d)',
          borderRadius: isMobile ? '16px 16px 0 0' : 'var(--ds-r-lg, 20px)',
          boxShadow: 'var(--ds-shadow-2)',
          backdropFilter: 'blur(14px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--ds-line-soft, #2a161b)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: accentGold, fontFamily: "'Cinzel', serif", letterSpacing: '1px', textTransform: 'uppercase' }}>
              🔗 Linked Storylines
            </div>
            <div style={{ fontSize: '11px', color: textMuted, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentTitle ? `for "${currentTitle}"` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--ds-ink-low, #9c8983)', cursor: 'pointer',
            fontSize: '22px', lineHeight: 1, padding: '4px 6px', flexShrink: 0,
            minHeight: '40px', minWidth: '40px',
          }}>×</button>
        </div>

        {/* Explainer */}
        <div style={{ padding: '10px 16px', fontSize: '11px', color: 'var(--ds-ink-low, #9c8983)', lineHeight: 1.5, borderBottom: '1px solid var(--ds-line-soft, #2a161b)' }}>
          Linked conversations share continuity. When you write here, the AI pulls
          compressed memory — key facts, where the linked scenes stand, threads still
          open — from everything linked below. It never pulls raw transcripts, so it
          stays cheap.
        </div>

        {/* Filter */}
        <div style={{ padding: '10px 16px 6px' }}>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name…"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--ds-input, #0d0708)', color: 'var(--ds-ink-hi, #f1e7e3)',
              border: '1px solid var(--ds-line, #3a2128)', borderRadius: 'var(--ds-r-md, 14px)',
              padding: '9px 11px', fontSize: '13px', outline: 'none',
              fontFamily: "'Cormorant Garamond', Georgia, serif",
            }}
          />
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 16px 16px' }}>
          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: textMuted, fontSize: '13px', fontStyle: 'italic' }}>
              Loading conversations…
            </div>
          ) : (
            <>
              {linked.length > 0 && (
                <>
                  <div style={{ fontSize: '10px', color: '#7aa2f7', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700, margin: '8px 2px 8px' }}>
                    Linked ({linked.length})
                  </div>
                  {linked.map(row)}
                </>
              )}
              <div style={{ fontSize: '10px', color: 'var(--ds-ink-low, #9c8983)', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700, margin: `${linked.length ? '16px' : '8px'} 2px 8px` }}>
                {linked.length ? 'Link more' : 'Your other conversations'}
              </div>
              {unlinked.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--ds-ink-low, #9c8983)', fontSize: '12px', fontStyle: 'italic' }}>
                  {filter.trim() ? 'Nothing matches that filter.' : 'No other conversations to link yet.'}
                </div>
              ) : (
                unlinked.map(row)
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
